import Vue from 'vue'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import i18n from '@/lang'

/**
 * 添付ファイルのダウンロード（API から arraybuffer で受け取る方式）を、
 * 画面全体をブロックせずに行うための共通処理（NWS_SMILELINE-1936）。
 *
 * - 進捗は画面右下の小さな表示（components/DownloadProgress.vue）に出し、キャンセルできる
 * - 同時に走らせるのは MAX_CONCURRENT_DOWNLOADS 本まで。それを超えた分は順番待ちにし、
 *   前が終わったら押された順に始める（順番待ちの間もキャンセルできる）
 * - 通信タイムアウト: 「DOWNLOAD_IDLE_TIMEOUT_MS の間、データが 1 バイトも届かない」ときに打ち切る
 *   （順番待ちの間は数えず、実際に要求を始めてから数える）
 * - 失敗時（キャンセル以外）は同じ表示欄に日本語／英語の文言を出す。
 *   通信断・タイムアウトとサーバーの失敗（応答あり）とで文言を分ける
 * - ログアウトしたら、順番待ち・実行中をすべて取り消す（cancelAllDownloads。呼び出しは App.vue）
 */

/**
 * 同時に走らせるダウンロードの本数（1 本）。
 *
 * 2 本以上にしない理由:
 * - API 側は 1 本ごとに画像・動画の作り直し（GD・ffmpeg）で Apache の処理枠とメモリを使う
 *   （本番は 2 台 × 20 枠）。ギャラリーで 10 枚続けて押しても API に届くのは 1 本ずつにする
 * - ブラウザ側は 1 本ごとに arraybuffer とその写し（Blob）を丸ごと抱える。
 *   共有フォルダの ZIP は最大 500MB で、iPhone の Safari では 2 本並ぶだけでタブが落ちうる
 * 押した分はすべて順番待ちに入り、最後までダウンロードされる（受け付けを断ることはしない）。
 */
export const MAX_CONCURRENT_DOWNLOADS = 1

/**
 * 保存のために作った Blob URL を解放するまでの時間（40 秒）。
 *
 * a[download] のクリック直後に解放すると、保存の確認を出すブラウザ（iPhone の Safari の
 * 「ダウンロードしますか？」など）で、利用者が押すより先に中身が消えて保存に失敗する。
 * FileSaver.js が同じ理由で 40 秒をとっているのに合わせた。解放しないと画面を閉じるまで
 * ファイル 1 個分のメモリが残り続ける（NWS_SMILELINE-1936 レビュー指摘）。
 */
export const OBJECT_URL_REVOKE_DELAY_MS = 40 * 1000

/** 保存に使った Blob URL を、少し待ってから解放する */
export function revokeObjectUrlLater(url: string) {
  setTimeout(() => globalThis.URL.revokeObjectURL(url), OBJECT_URL_REVOKE_DELAY_MS)
}

/**
 * 通信タイムアウト（60 秒）。
 *
 * 値の根拠:
 * - アプリ版の同じダウンロードは 60 秒でタイムアウトにしている
 *   （mobile lib/core/configs/app_configs.dart の apiTimeout を dio の receiveTimeout に設定。
 *   dio の receiveTimeout は「受信データの間隔」で判定するため、ここと同じ考え方）
 * - API の前段のロードバランサ（AWS ALB）のアイドルタイムアウトの既定値も 60 秒
 *   （本番の設定値は未確認）。既定のままなら、60 秒間 1 バイトも返らない要求はどのみち切られる
 *
 * axios の `timeout`（XHR の timeout）は「転送全体の所要時間」で打ち切るため、
 * 共有フォルダの最大 500MB の ZIP を回線の遅い端末で正常に受け取っている途中でも切ってしまう。
 * そのため、ここでは「データが止まってからの時間」で判定する（受信のたびに計り直す）。
 */
export const DOWNLOAD_IDLE_TIMEOUT_MS = 60 * 1000

/** 失敗の文言を自動で消すまでの時間 */
export const DOWNLOAD_ERROR_DISPLAY_MS = 10 * 1000

/**
 * 失敗の種類
 * - timeout: 無通信のまま DOWNLOAD_IDLE_TIMEOUT_MS が過ぎた
 * - failed : 応答が無い（通信断など）
 * - server : 応答はあったが失敗（500・404・401 など）
 */
export type DownloadFailureKind = 'timeout' | 'failed' | 'server'

export interface DownloadTask {
  id: number
  key: string
  label: string
  /** false の間は順番待ち（まだ要求していない） */
  started: boolean
  loaded: number
  total: number
  cancel: () => void
}

export interface DownloadError {
  id: number
  /** 文言にファイル名が入らない場合に、見出しとして出すファイル名 */
  label: string
  message: string
}

export const downloadState = Vue.observable({
  tasks: [] as DownloadTask[],
  errors: [] as DownloadError[]
})

export interface DownloadOptions {
  /** 同じファイルの二重ダウンロードを防ぐためのキー（省略時は label） */
  key?: string
  /**
   * HTTP エラー（応答あり）を呼び出し元が独自に表示する場合に渡す。
   * true を返したときは共通の失敗文言を出さない（二重表示を避ける）。
   */
  handleHttpError?: (err: AxiosError) => boolean
  /**
   * handleHttpError を持つ画面のコンポーネント。応答が返る前に、この画面が破棄されていたら、
   * または押したときと画面のパスが変わっていたら、handleHttpError を呼ばない（別の画面にダイアログが出るのを防ぐ）。
   * パスも見るのは、掲示板 A → 掲示板 B、ファイル 5 → ファイル 6 のように同じ画面の部品が使い回され、
   * 破棄されないまま別の掲示板・ファイルの表示に変わるため。
   * その場合も、右下の共通の失敗文言は出す（どのファイルが失敗したかは伝える）
   */
  owner?: Vue
}

/** 利用者がキャンセルしたことを表す理由 */
const REASON_USER_CANCEL = 'download canceled by user'
const REASON_TIMEOUT = 'download idle timeout'

let sequence = 0

/** 要求を始める関数（順番待ちのタスクの分だけ持つ） */
const starters: { [id: number]: () => void } = {}

/** 表示用のファイル名（複数なら「先頭のファイル名 ほか N 件」） */
export function downloadLabel(fileNames: string[]): string {
  const names = fileNames.filter(name => !!name)
  if (!names.length) return ''
  if (names.length === 1) return names[0]
  return i18n.t('common.message.download_label_multiple', {
    name: names[0],
    count: names.length - 1
  }) as string
}

/** 失敗の種類を判定する（テスト手順書と合わせるため外へ出している） */
export function classifyDownloadError(err: any, timedOut: boolean): DownloadFailureKind | 'canceled' | 'http' {
  if (timedOut) return 'timeout'
  if (axios.isCancel(err)) return 'canceled'
  // XHR 自体の timeout（ECONNABORTED）も念のためタイムアウト扱いにする
  if (err && err.code === 'ECONNABORTED') return 'timeout'
  if (err && err.response) return 'http'
  return 'failed'
}

export function showDownloadError(kind: DownloadFailureKind, label: string) {
  let error: DownloadError
  if (kind === 'server') {
    // サーバーの失敗は通信環境の問題ではないため、既存の汎用エラー文言を使い、ファイル名は見出しで出す
    error = {
      id: ++sequence,
      label,
      message: i18n.t('common.message.uncatch_error') as string
    }
  } else {
    const key = kind === 'timeout' ? 'common.message.download_timeout' : 'common.message.download_failed'
    error = {
      id: ++sequence,
      label: '',
      message: i18n.t(key, { name: label }) as string
    }
  }
  downloadState.errors.push(error)
  setTimeout(() => dismissDownloadError(error.id), DOWNLOAD_ERROR_DISPLAY_MS)
}

export function dismissDownloadError(id: number) {
  const index = downloadState.errors.findIndex(error => error.id === id)
  if (index >= 0) downloadState.errors.splice(index, 1)
}

/**
 * ダウンロード要求を実行する。
 *
 * @param label   進捗・失敗の表示に使うファイル名
 * @param request 受け取った config（cancelToken / onDownloadProgress）をそのまま axios に渡して要求する関数
 * @returns 成功時は応答。キャンセル・タイムアウト・通信失敗・共通表示済みの HTTP エラーでは null
 *          （handleHttpError が true を返した HTTP エラーも null。呼び出し元の then だけ書けばよい）
 */
export function runDownload(
  label: string,
  request: (config: AxiosRequestConfig) => Promise<AxiosResponse>,
  options: DownloadOptions = {}
): Promise<AxiosResponse | null> {
  const key = options.key || label
  // 押したときの画面のパス（応答が返ったときに、まだ同じ画面を見ているかの判定に使う）
  const ownerPath = options.owner ? options.owner.$route.path : ''

  // 同じファイルのダウンロード中（順番待ちを含む）に、もう一度押された場合は重ねて要求しない
  // （以前は全画面ブロックで二度押しできなかったため、その代わり）
  if (downloadState.tasks.some(task => task.key === key)) {
    return Promise.resolve(null)
  }

  return new Promise<AxiosResponse | null>(resolve => {
    const source = axios.CancelToken.source()
    let timedOut = false
    let settled = false
    // 取り消し済み（利用者のキャンセル・ログアウト）。取り消しと応答が行き違っても、
    // 保存も失敗の表示もしない（ログイン画面に前の人のファイル名を出さないため）
    let canceled = false
    let idleTimer: ReturnType<typeof setTimeout> | undefined

    const restartIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer)
      idleTimer = setTimeout(() => {
        timedOut = true
        source.cancel(REASON_TIMEOUT)
      }, DOWNLOAD_IDLE_TIMEOUT_MS)
    }

    const finish = (result: AxiosResponse | null) => {
      if (settled) return
      settled = true
      if (idleTimer) clearTimeout(idleTimer)
      delete starters[task.id]
      const index = downloadState.tasks.findIndex(item => item.id === task.id)
      if (index >= 0) downloadState.tasks.splice(index, 1)
      resolve(result)
      // 枠が 1 つ空いたので、順番待ちの先頭を始める
      startWaitingTasks()
    }

    const start = () => {
      task.started = true
      restartIdleTimer()
      let promise: Promise<AxiosResponse>
      try {
        promise = request({
          cancelToken: source.token,
          onDownloadProgress: (event: ProgressEvent) => {
            task.loaded = event.loaded
            task.total = event.lengthComputable ? event.total : 0
            restartIdleTimer()
          }
        })
      } catch (err) {
        promise = Promise.reject(err)
      }
      promise
        .then(res => finish(canceled ? null : res))
        .catch(err => {
          const kind = canceled ? 'canceled' : classifyDownloadError(err, timedOut)
          finish(null)
          if (kind === 'canceled') return
          if (kind === 'http') {
            const ownerAlive =
              !options.owner ||
              (!(options.owner as any)._isDestroyed && options.owner.$route.path === ownerPath)
            if (ownerAlive && options.handleHttpError && options.handleHttpError(err)) return
            // 429 は共通の割り込み処理（interceptors）が専用の画面を出すため、ここでは出さない
            if (err.response.status === 429) return
            showDownloadError('server', label)
            return
          }
          showDownloadError(kind, label)
        })
    }

    const task: DownloadTask = {
      id: ++sequence,
      key,
      label,
      started: false,
      loaded: 0,
      total: 0,
      cancel: () => {
        canceled = true
        if (task.started) {
          source.cancel(REASON_USER_CANCEL)
        } else {
          // 順番待ちのまま取り消した場合は、要求をせずに終える
          finish(null)
        }
      }
    }
    starters[task.id] = start
    downloadState.tasks.push(task)
    startWaitingTasks()
  })
}

/**
 * 順番待ちと実行中のダウンロードを、すべて取り消す（ログアウト時。NWS_SMILELINE-1936 レビュー指摘）。
 *
 * 状態はモジュールで 1 つのため、取り消さないと、ログアウト後も残りがトークン無しで要求されて
 * ログイン画面に前の人のファイル名入りの失敗が出たり、次の人がログインした後にその人のトークンで
 * 要求されたりする（共有の PC）。
 * - 順番待ちを先に取り消す（実行中を先に取り消すと、空いた枠で順番待ちの先頭が始まってしまう）
 * - 取り消しなので失敗の文言は出さず、表示中の失敗の文言（前の人のファイル名入り）も消す
 */
export function cancelAllDownloads() {
  const tasks = downloadState.tasks.slice()
  tasks.filter(task => !task.started).forEach(task => task.cancel())
  tasks.filter(task => task.started).forEach(task => task.cancel())
  downloadState.errors.splice(0)
}

/** 空いている枠の数だけ、順番待ちのタスクを押された順に始める */
function startWaitingTasks() {
  let running = downloadState.tasks.filter(task => task.started).length
  for (const task of downloadState.tasks) {
    if (running >= MAX_CONCURRENT_DOWNLOADS) break
    if (task.started) continue
    const start = starters[task.id]
    delete starters[task.id]
    if (start) {
      start()
      running++
    }
  }
}
